import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, TrendingUp, TrendingDown, Minus, Star } from 'lucide-react';
import PageSeo from '../components/PageSeo';
import SchemaMarkup from '../components/SchemaMarkup';
import { pageSchemas } from '../data/pageSchemas';

const GiftCardValues = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    // Analytics only (SEO metadata handled by PageSeo)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_view', {
        'page_title': '2024 Gift Card Values and Rates Directory',
        'page_location': window.location.href,
        'event_category': 'seo_content'
      });
    }
  }, []);

  // Gift card data with current market rates
  const giftCards = [
    {
      brand: 'Amazon',
      category: 'retail',
      rate: '85-93%',
      trend: 'up',
      popularity: 5,
      demand: 'Very High',
      description: 'Highest value and most liquid gift card in the market',
      bestTime: 'Year-round',
      tips: 'Always in high demand, consistently top rates'
    },
    {
      brand: 'Apple/iTunes',
      category: 'digital',
      rate: '80-90%',
      trend: 'up',
      popularity: 5,
      demand: 'Very High',
      description: 'Premium rates for App Store and iTunes credit',
      bestTime: 'Product launches, holidays',
      tips: 'Higher rates during iPhone/iPad launch seasons'
    },
    {
      brand: 'Google Play',
      category: 'digital',
      rate: '75-85%',
      trend: 'steady',
      popularity: 4,
      demand: 'High',
      description: 'Popular for Android users and mobile gamers',
      bestTime: 'Gaming seasons, holidays',
      tips: 'Rates increase during major game releases'
    },
    {
      brand: 'Walmart',
      category: 'retail',
      rate: '82-88%',
      trend: 'up',
      popularity: 4,
      demand: 'High',
      description: 'Strong demand for everyday shopping needs',
      bestTime: 'Back-to-school, holidays',
      tips: 'Peak rates during major shopping seasons'
    },
    {
      brand: 'Target',
      category: 'retail',
      rate: '80-87%',
      trend: 'steady',
      popularity: 4,
      demand: 'High',
      description: 'Popular for home goods and fashion',
      bestTime: 'Back-to-school, spring',
      tips: 'Higher rates for larger denominations ($100+)'
    },
    {
      brand: 'Best Buy',
      category: 'electronics',
      rate: '78-85%',
      trend: 'up',
      popularity: 4,
      demand: 'High',
      description: 'Electronics and gaming gift cards',
      bestTime: 'Black Friday, gaming seasons',
      tips: 'Peak demand during electronics sales events'
    },
    {
      brand: 'Home Depot',
      category: 'home',
      rate: '82-88%',
      trend: 'up',
      popularity: 4,
      demand: 'High',
      description: 'High value for home improvement projects',
      bestTime: 'Spring, summer',
      tips: 'Best rates during home improvement seasons'
    },
    {
      brand: 'Starbucks',
      category: 'food',
      rate: '75-82%',
      trend: 'steady',
      popularity: 3,
      demand: 'Medium',
      description: 'Popular coffee chain gift cards',
      bestTime: 'Morning hours, holidays',
      tips: 'Consistent demand throughout the year'
    },
    {
      brand: 'Nike',
      category: 'fashion',
      rate: '78-85%',
      trend: 'up',
      popularity: 4,
      demand: 'High',
      description: 'Premium athletic and fashion brand',
      bestTime: 'Back-to-school, fitness seasons',
      tips: 'Higher rates during athletic events/seasons'
    },
    {
      brand: 'Steam',
      category: 'gaming',
      rate: '75-82%',
      trend: 'steady',
      popularity: 4,
      demand: 'High',
      description: 'PC gaming platform credits',
      bestTime: 'Summer/winter sales',
      tips: 'Peak demand during Steam sales events'
    },
    {
      brand: 'Xbox',
      category: 'gaming',
      rate: '75-82%',
      trend: 'up',
      popularity: 4,
      demand: 'High',
      description: 'Gaming console and digital content',
      bestTime: 'Game releases, holidays',
      tips: 'Higher rates during new console launches'
    },
    {
      brand: 'PlayStation',
      category: 'gaming',
      rate: '75-82%',
      trend: 'up',
      popularity: 4,
      demand: 'High',
      description: 'PlayStation Network credits',
      bestTime: 'Game releases, holidays',
      tips: 'Peak rates during exclusive game launches'
    },
    {
      brand: 'eBay',
      category: 'marketplace',
      rate: '70-80%',
      trend: 'steady',
      popularity: 3,
      demand: 'Medium',
      description: 'Online marketplace credits',
      bestTime: 'Year-round',
      tips: 'Steady demand from resellers'
    },
    {
      brand: 'Costco',
      category: 'warehouse',
      rate: '80-86%',
      trend: 'up',
      popularity: 3,
      demand: 'Medium-High',
      description: 'Wholesale club shopping credits',
      bestTime: 'Holiday seasons',
      tips: 'Higher rates for membership holders'
    },
    {
      brand: 'Lowe\'s',
      category: 'home',
      rate: '80-86%',
      trend: 'steady',
      popularity: 3,
      demand: 'Medium-High',
      description: 'Home improvement and gardening',
      bestTime: 'Spring, summer',
      tips: 'Best rates during gardening season'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Categories', count: giftCards.length },
    { id: 'retail', name: 'Retail', count: giftCards.filter(card => card.category === 'retail').length },
    { id: 'digital', name: 'Digital/Tech', count: giftCards.filter(card => card.category === 'digital').length },
    { id: 'gaming', name: 'Gaming', count: giftCards.filter(card => card.category === 'gaming').length },
    { id: 'electronics', name: 'Electronics', count: giftCards.filter(card => card.category === 'electronics').length },
    { id: 'food', name: 'Food & Dining', count: giftCards.filter(card => card.category === 'food').length },
    { id: 'fashion', name: 'Fashion', count: giftCards.filter(card => card.category === 'fashion').length },
    { id: 'home', name: 'Home & Garden', count: giftCards.filter(card => card.category === 'home').length },
  ];

  const filteredCards = giftCards.filter(card => {
    const matchesSearch = card.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || card.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="text-green-500" size={20} />;
      case 'down': return <TrendingDown className="text-red-500" size={20} />;
      default: return <Minus className="text-gray-500" size={20} />;
    }
  };

  const getPopularityStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star 
        key={index} 
        size={16} 
        className={index < rating ? 'text-yellow-500 fill-current' : 'text-gray-300'} 
      />
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      <PageSeo
        title="Gift Card Values & Exchange Rates | CashifyGCMart"
        description="Current gift card exchange rates. See real-time values for Amazon, Apple, Walmart, Target and 100+ brands. Updated daily market prices and trends."
        path="/gift-card-values"
      />
      <SchemaMarkup faqs={pageSchemas.giftCardValues.faqs} />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            2024 Gift Card Values & Exchange Rates
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            Current market rates for 100+ gift card brands - Updated daily
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-white/20 px-4 py-2 rounded-full">📊 Real-time Rates</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">📈 Market Trends</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">💰 Best Prices</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Search and Filter Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search gift card brands..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.count})
                </option>
              ))}
            </select>
          </div>

          {/* Market Summary */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Market Summary - October 2024</h2>
            <div className="grid md:grid-cols-4 gap-4 text-center">
              <div className="bg-white p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">93%</div>
                <div className="text-sm text-gray-600">Highest Rate (Amazon)</div>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">85%</div>
                <div className="text-sm text-gray-600">Average Rate</div>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">100+</div>
                <div className="text-sm text-gray-600">Brands Accepted</div>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">24hr</div>
                <div className="text-sm text-gray-600">Average Payout</div>
              </div>
            </div>
          </div>
        </div>

        {/* Gift Cards Grid */}
        <div className="grid gap-6">
          {filteredCards.map((card, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between">
                <div className="flex-1 mb-4 lg:mb-0">
                  <div className="flex items-center mb-2">
                    <h3 className="text-xl font-bold text-gray-800 mr-3">{card.brand}</h3>
                    <div className="flex items-center space-x-2">
                      {getTrendIcon(card.trend)}
                      <div className="flex">{getPopularityStars(card.popularity)}</div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-2">{card.description}</p>
                  
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="bg-gray-100 px-3 py-1 rounded-full">
                      <strong>Demand:</strong> {card.demand}
                    </span>
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                      <strong>Best Time:</strong> {card.bestTime}
                    </span>
                  </div>
                  
                  <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded-r">
                    <p className="text-sm text-gray-700">
                      <strong>💡 Tip:</strong> {card.tips}
                    </p>
                  </div>
                </div>
                
                <div className="text-center lg:text-right lg:ml-6">
                  <div className="text-3xl font-bold text-green-600 mb-2">{card.rate}</div>
                  <div className="text-sm text-gray-500 mb-4">Current Rate</div>
                  <Link 
                    to="/rate-calculator" 
                    className="inline-block bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Get Quote
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Rate Factors Section */}
        <div className="mt-12 bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">What Affects Gift Card Rates?</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-purple-600">Factors That Increase Rates</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="bg-green-500 text-white rounded-full p-1 mt-1">
                    <TrendingUp size={12} />
                  </div>
                  <div>
                    <strong>High Brand Demand:</strong> Popular brands like Amazon, Apple
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-green-500 text-white rounded-full p-1 mt-1">
                    <TrendingUp size={12} />
                  </div>
                  <div>
                    <strong>Seasonal Trends:</strong> Holiday seasons, back-to-school
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-green-500 text-white rounded-full p-1 mt-1">
                    <TrendingUp size={12} />
                  </div>
                  <div>
                    <strong>Higher Denominations:</strong> $100+ cards often get better rates
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-green-500 text-white rounded-full p-1 mt-1">
                    <TrendingUp size={12} />
                  </div>
                  <div>
                    <strong>Market Competition:</strong> Multiple buyers competing
                  </div>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4 text-red-600">Factors That Decrease Rates</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="bg-red-500 text-white rounded-full p-1 mt-1">
                    <TrendingDown size={12} />
                  </div>
                  <div>
                    <strong>Low Demand Brands:</strong> Lesser-known or niche retailers
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-red-500 text-white rounded-full p-1 mt-1">
                    <TrendingDown size={12} />
                  </div>
                  <div>
                    <strong>Partially Used Cards:</strong> Cards with remaining partial balances
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-red-500 text-white rounded-full p-1 mt-1">
                    <TrendingDown size={12} />
                  </div>
                  <div>
                    <strong>Market Oversupply:</strong> Too many cards of the same brand
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-red-500 text-white rounded-full p-1 mt-1">
                    <TrendingDown size={12} />
                  </div>
                  <div>
                    <strong>Expiration Risk:</strong> Cards close to expiration date
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get the Best Rate for Your Gift Card?</h2>
          <p className="text-xl mb-6">Use our calculator to see real-time rates for your specific cards</p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <Link to="/rate-calculator" className="block sm:inline-block bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Calculate Value Now
            </Link>
            <Link to="/form-submission" className="block sm:inline-block bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-colors border border-white">
              Start Selling
            </Link>
          </div>
        </div>

        {/* SEO content — long-form guide to gift card resale values */}
        <section className="mt-16 bg-white rounded-2xl shadow-sm p-8 sm:p-10 prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What really determines the resale value of a gift card?</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The resale price of a gift card is not a mysterious number pulled out of thin air. It's set by a
            handful of very predictable factors, and once you know them you can estimate roughly what any card
            is worth before you even hit the calculator. Below is the honest breakdown, straight from the
            desk that actually prices these cards every day.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1. Demand for the brand</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            This is the single biggest factor. Cards from retailers with broad everyday use — Amazon,
            Walmart, Target, Visa, Apple, Starbucks — resell almost instantly, so we can afford to pay the
            strongest rates. Cards from niche or regional retailers resell more slowly, which pulls the rate
            down a few percentage points.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2. Card format (digital vs. physical)</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Digital cards are more valuable because they can be redeemed instantly and shipped to the next
            buyer with zero cost. Physical cards cost us a little to handle, store, and resell, so they pay
            slightly less — usually 2–4 percentage points under the digital rate for the same brand.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3. Balance size</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Larger balances typically pay a hair more per dollar because our fixed processing cost is spread
            across more money. A $500 Amazon card will usually get a slightly better rate than five $100
            Amazon cards from the same person. That said, the difference is small — convenience should
            outweigh squeezing the last 0.5%.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4. Market timing</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Rates move with supply and demand. After the holidays, the supply of gift cards spikes and prices
            dip. Around back-to-school and the summer gifting season, demand rises and rates often tick up.
            The difference is usually within 2–3 percentage points for the same brand across a year.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5. Proof of purchase</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Attaching a dated receipt can unlock a small rate bonus because it reassures the fraud engine
            that the card was legitimately purchased. If you still have the email or paper receipt from the
            original buy, upload it — it almost always pays for itself.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">A practical example</h3>
          <p className="text-gray-700 leading-relaxed">
            A $200 digital Amazon card with receipt will typically pay around 82–85% depending on the week.
            That same $200 as a physical Amazon card without a receipt will pay around 78–81%. And a $200
            card from a smaller regional retailer might pay 55–65%. The calculator will show the exact live
            number — these ranges are just so you can set expectations before you load the form.
          </p>
        </section>
      </div>
    </div>
  );
};

export default GiftCardValues;